/**
 * useWorkerStatus — Single source of truth for worker status at a station.
 *
 * Fetches from the assignments endpoint and listens for WebSocket events
 * to keep the status in real-time. Returns operator and packer status
 * including active, on-break, and scanned-out states.
 *
 * Also provides action methods (takeBreak, clockOut) so all views use
 * the same break/clock-out logic.
 *
 * Usage:
 *   const { operator, packer, sessionStatus, takeBreak, clockOut, isLoading, refetch } = useWorkerStatus(stationId, subSessionNumber);
 */
import { useState, useEffect, useCallback } from 'react';
import { wsService } from '../services/websocket';
import api from '../services/api';
import operatorService from '../services/operatorService';

export interface WorkerInfo {
  user_id: string;
  employee_number: string;
  name: string;
  clocked_in_at: string;
  on_break: boolean;
}

export interface WorkerStatus {
  operator: WorkerInfo | null;
  packer: WorkerInfo | null;
  /** Derived session status: 'active' if any worker active, 'paused' if all on break, null if unknown */
  sessionStatus: 'active' | 'paused' | null;
  isLoading: boolean;
  refetch: () => void;
  /** Take break: pause session so session timing stops immediately for the whole sub-session */
  takeBreak: (employeeNumber: string, displayName: string, role: 'operator' | 'packer') => Promise<void>;
  /** Clock out: clock out without break (clears assignment) */
  clockOut: (employeeNumber: string) => Promise<void>;
}

export function useWorkerStatus(
  stationId: number | null | undefined,
  subSessionNumber: string | null | undefined,
  /** Only fetch when enabled (default true). Set false to defer until needed. */
  enabled: boolean = true
): WorkerStatus {
  const [operator, setOperator] = useState<WorkerInfo | null>(null);
  const [packer, setPacker] = useState<WorkerInfo | null>(null);
  const [sessionStatus, setSessionStatus] = useState<'active' | 'paused' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!stationId || !enabled) return;
    setIsLoading(true);
    try {
      const params = subSessionNumber ? `?sub_session_number=${subSessionNumber}` : '';
      const response = await api.get(`/operator/clockin/assignments/${stationId}${params}`);
      if (response.data.success) {
        const op = response.data.assignments?.operator || null;
        const pk = response.data.assignments?.packer || null;
        setOperator(op);
        setPacker(pk);

        // Derive session status from worker states
        const anyActive = (op && !op.on_break) || (pk && !pk.on_break);
        const anyPresent = op || pk;
        const allOnBreak = anyPresent && (!op || op.on_break) && (!pk || pk.on_break);
        if (anyActive) setSessionStatus('active');
        else if (allOnBreak) setSessionStatus('paused');
        else setSessionStatus(null);
      }
    } catch {
      // Non-critical — keep previous state
    } finally {
      setIsLoading(false);
    }
  }, [stationId, subSessionNumber, enabled]);

  // Fetch on mount and when station/sub-session changes
  useEffect(() => {
    if (enabled) fetchStatus();
  }, [fetchStatus, enabled]);

  // Listen for WebSocket events to keep status in real-time
  useEffect(() => {
    if (!stationId || !enabled) return;
    wsService.connect();

    const handleUpdate = (data: any) => {
      if (data?.station_id === stationId ||
          data?.sub_session_number === subSessionNumber) {
        fetchStatus();
      }
    };

    wsService.on('worker-clocked-in', handleUpdate);
    wsService.on('worker-clocked-out', handleUpdate);
    wsService.on('production-session-updated', handleUpdate);
    wsService.on('station-updated', handleUpdate);

    return () => {
      wsService.off('worker-clocked-in', handleUpdate);
      wsService.off('worker-clocked-out', handleUpdate);
      wsService.off('production-session-updated', handleUpdate);
      wsService.off('station-updated', handleUpdate);
    };
  }, [stationId, subSessionNumber, enabled, fetchStatus]);

  // Take break: pause session and let the backend manage break state.
  const takeBreak = useCallback(async (employeeNumber: string, displayName: string, role: 'operator' | 'packer') => {
    if (!subSessionNumber || !stationId) return;
    await operatorService.pauseSession(
      subSessionNumber,
      `Break taken by ${displayName}`,
      true,
      employeeNumber,
      role
    );
    await fetchStatus();
  }, [subSessionNumber, stationId, fetchStatus]);

  // Clock out: scan out without break (clears assignment)
  const clockOut = useCallback(async (employeeNumber: string) => {
    if (!stationId) return;
    await operatorService.clockOut(`@+${employeeNumber}`, stationId);
    await fetchStatus();
  }, [stationId, fetchStatus]);

  return { operator, packer, sessionStatus, isLoading, refetch: fetchStatus, takeBreak, clockOut };
}
